import { useCallback, useRef, useState } from "react";
import { flushSync } from "react-dom";

// How long a locked field is forced readOnly right at the start of a tap —
// see suppressKeyboardOnPointerDown below for why.
const TAP_READONLY_MS = 100;

// On tablets, a single tap should place the cursor in a field — but the
// on-screen keyboard should only open when the user deliberately asks for it
// via the field's keyboard button.
//
// inputmode="none" is the HTML attribute built for exactly this (focus
// normally, cursor visible, but suppress the on-screen keyboard) — but it's
// only a hint, and proved unreliable by itself across several fields on
// Android/Samsung Internet: some fields kept popping the keyboard on the
// very first tap regardless. readOnly is honored far more consistently for
// suppressing the on-screen keyboard (there's nothing editable to invoke an
// IME for) — but it can't just be set while locked, because it would also
// block a barcode scanner's keystrokes (a scanner types into the field like
// a physical keyboard, indistinguishable from one at the DOM level).
//
// The fix used here: force readOnly on for a brief moment starting at
// mousedown/touchstart — i.e. exactly bracketing the moment a real tap
// resolves into a focus event, which is when the browser actually decides
// whether to summon the on-screen keyboard — then release it. A scanner
// never fires mousedown/touchstart (it only sends keystrokes into whatever
// already has focus), so this can never block scanner input; it only ever
// engages around a genuine touch tap.
const suppressKeyboardOnPointerDown = (event) => {
  const el = event.currentTarget;
  el.readOnly = true;
  window.setTimeout(() => {
    el.readOnly = false;
  }, TAP_READONLY_MS);
};

// When the keyboard button is pressed we need the browser to re-evaluate
// inputMode and actually summon the keyboard. Just flipping the attribute on
// the already-focused element and calling blur()+focus() on it turned out to
// be unreliable — different mobile keyboard controllers handle that same-
// element focus transition inconsistently, which is why it only worked
// "sometimes". Forcing a full remount (a fresh `key`, via cKeyReset) instead
// guarantees a genuinely new element that was never in the "no keyboard"
// state to begin with — focusing it behaves like any normal first-time
// focus, which reliably opens the keyboard every time.
//
// A no-op (returns {}) / reset key 0 when `active` is false, e.g. on
// non-touch/laptop devices where this restriction isn't wanted.
const useKeyboardUnlock = (active) => {
  const [unlockedId, setUnlockedId] = useState(null);
  const [resetKeys, setResetKeys] = useState({});
  // The unlock remount below unmounts the old (locked) element, which the
  // browser treats as an implicit blur on it — without this guard, that
  // blur would fire our onBlur handler and immediately re-lock the field
  // again. Tracks which field's blur, if any, is "ours" (from our own
  // remount) and should be ignored rather than treated as the user
  // genuinely leaving the field.
  const suppressBlurRef = useRef(null);

  const getResetKey = useCallback(
    (fieldId) => resetKeys[fieldId] || 0,
    [resetKeys]
  );

  const getFieldProps = useCallback(
    (fieldId) => {
      if (!active) {
        return {};
      }
      const locked = unlockedId !== fieldId;
      return {
        inputMode: locked ? "none" : undefined,
        onMouseDown: locked ? suppressKeyboardOnPointerDown : undefined,
        onTouchStart: locked ? suppressKeyboardOnPointerDown : undefined,
        onBlur: () => {
          if (suppressBlurRef.current === fieldId) {
            return;
          }
          setUnlockedId((prev) => (prev === fieldId ? null : prev));
        },
      };
    },
    [active, unlockedId]
  );

  // Wired to each field's keyboard button (see CInput2's cOnUnlock) instead
  // of a double-tap gesture — a button press is unambiguous and doesn't
  // depend on the platform delivering two clicks within a timing window,
  // which some tablet browsers were swallowing as a native zoom/selection
  // gesture instead of passing through to JS.
  const unlockField = useCallback((fieldId) => {
    suppressBlurRef.current = fieldId;
    // flushSync forces the remount (new key, inputMode cleared) to commit
    // synchronously, still inside this click handler's call stack — a
    // setTimeout (even 0ms) defers focus() into a detached macrotask, and
    // mobile browsers only reliably attach the on-screen keyboard to a
    // focus() call made within a trusted user-gesture stack. That mismatch
    // is what let the keyboard appear while the actual caret/focus never
    // landed.
    flushSync(() => {
      setUnlockedId(fieldId);
      setResetKeys((prev) => ({
        ...prev,
        [fieldId]: (prev[fieldId] || 0) + 1,
      }));
    });
    const el = document.getElementById(fieldId);
    if (el) {
      el.focus();
      // Focusing a freshly-remounted field doesn't always leave a visible
      // caret on its own on some mobile browsers — force one by explicitly
      // placing the selection at the end.
      const len = el.value ? el.value.length : 0;
      if (typeof el.setSelectionRange === "function") {
        el.setSelectionRange(len, len);
      }
    }
    suppressBlurRef.current = null;
  }, []);

  return { getFieldProps, getResetKey, unlockField };
};

export default useKeyboardUnlock;
